﻿import { ApiCaller, FullfilledApiResponse } from 'apis/ApiCaller';
import { PaginatedResult } from 'apis/TransactionApi';
import { CurrentUserPartners } from 'apis/StudentApi';

/** Valid sort column names accepted by the Users GetAllPaged endpoint. */
export type UserSortField =
    | 'ProfileID'
    | 'LoginID'
    | 'EmailAddress'
    | 'SecretQuestion'
    | 'SecretAnswer'
    | 'UserName'
    | 'PhoneNumber'
    | 'firstname'
    | 'lastname'
    | 'isActive'
    | 'InviteStatus'
    | 'roleID'
    | 'addUsers';

export interface NewUser extends User {
    PartnerList: UserPartner[];
    Password: string;
    Profile: 'Admin';
    SelectedPartner: CurrentUserPartners;
}

export interface User {
    CurrentPartnerId: number;
    CurrentUserId: number;
    DisabledDetailsBtn: boolean;
    EarlyAccessPreference: EEarlyAccessPreference;
    Email: string;
    FeatureRingId: number;
    FirstName: string;
    FullName: string;
    Id: string;
    LastName: string;
    PartnerActive: boolean;
    PartnerInviteText: string;
    PartnerList: UserPartner[];
    Permissions: Permission[];
    PhoneNumber: string;
    UserName: string;
    UserProfileID: number;
}

export interface UserPartner extends User {
    Active: boolean;
    InviteStatus: number;
    InviteStatusText?: string;
    Profile: number;
}

type Permission = {
    Action: string;
    PermissionId: string;
    Reference: string;
};

// Hoping to remove when we bring back Roles and actually implement those.

export type OldPermission = {
    checked: boolean;
    Name: string;
    References: OldPermRef[];
}

export type OldPermRef = {
    actions: OldPermRefAct[];
    reference: string;
}

export type OldPermRefAct = {
    action: string;
    checked: boolean;
    MenuOptionID: number;
}

// END

export interface Role {
    Active?: boolean;
    Checked: boolean;
    Description: string;
    Name: string;
    Permissions: string[];
    ProfileId: number;
    RoleId: string;
    RoleProfileId: string;
}

export enum EEarlyAccessPreference {
    All = 0,
    OptIn = 1,
    None = 2,
}

export type PendingPartnerInvite = {
    invitationDate: string;
    invitationId: number;
    invitedByName: string;
    partnerId: number;
    partnerName: string;
};

export class UsersApi {

    private static apiCaller = new ApiCaller('Users');

    public static GetAll(cultureId = 1, partnerId: number | undefined = -1): Promise<User[]> {

        let passingObj: any = { cultureId };
        if (partnerId !== -1) {

            passingObj = { ...passingObj, passedInPartnerId: partnerId };

        }
        return this.apiCaller.GET<User[]>('GetAll', passingObj);

    }

    public static GetAllPaged(
        page = 1,
        pageSize = 25,
        sortField: UserSortField | '' = '',
        sortDir = '',
        searchTerm = '',
        partnerId = -1
    ): Promise<PaginatedResult<User>> {

        return this.apiCaller.GET<PaginatedResult<User>>('GetAllPaged', {
            page,
            pageSize,
            passedInPartnerId: partnerId,
            searchTerm,
            sortDir,
            sortField
        });

    }

    public static GetPermissions(userId: string, partnerId = -1): Promise<Permission[]> {

        return this.apiCaller.GET<Permission[]>('GetPermissions', {
            PartnerId: partnerId,
            UserId: userId
        });

    }

    public static Create(
        { FirstName, LastName, Password, UserName }: NewUser,
        PermissionCSV = '',
        PartnerId = -1,
        PartnerType = -1,
        AllowUserToSetPassword = true
    ): Promise<FullfilledApiResponse> {

        return this.apiCaller.PUT<void>('Create', {
            AllowUserToSetPassword,
            FirstName,
            LastName,
            PartnerId,
            PartnerType,

            // When the new admin will set their own password, send no password at all.
            Password: AllowUserToSetPassword ? '' : Password,
            PermissionCSV,
            UserName
        });

    }

    public static ResendWelcomeEmail(id: string): Promise<FullfilledApiResponse> {

        return this.apiCaller.POST<void>('ResendWelcomeEmail', { id });

    }

    public static CanResendWelcomeEmail(id: string): Promise<boolean> {

        return this.apiCaller.GET<boolean>('CanResendWelcomeEmail', { id });

    }

    public static Update(user: Partial<User>, PermissionCSV = '', PartnerId = -1, PartnerType = -1): Promise<void> {

        const { EarlyAccessPreference, Email, FirstName, Id, LastName, PartnerList } = user || {};

        return this.apiCaller.PUT<void>('Update', {
            Active: PartnerList?.[0]?.Active ?? true,
            EarlyAccessPreference,
            Email,
            FirstName,
            Id,
            LastName,
            PartnerId,
            PartnerType,
            PermissionCSV
        });

    }

    public static GetPermissionsWNames(cultureId = 1): Promise<any> {

        return this.apiCaller.GET<any>('GetPermissionsWNames', { cultureId });

    }

    public static GetEarlyAccessPreference(Id: string): Promise<EEarlyAccessPreference> {

        return this.apiCaller.GET<EEarlyAccessPreference>('GetEarlyAccessPreference', { Id });

    }

    public static GetRolesByProfileType(passedInPartnerType: number, cultureId: number): Promise<Role[]> {

        return this.apiCaller.GET<Role[]>('GetRolesByProfileType', { cultureId, passedInPartnerType });

    }

    public static GetAllPermissions(currentPartnerType = 0): Promise<OldPermission[]> {

        return this.apiCaller.GET('GetAllPermissions', { currentPartnerType });

    }

    //Resetting another admin's password
    public static ResetAdminPassword(Id: string, newPassword: string): Promise<string> {

        return this.apiCaller.PUT<string>('ResetAdminPassword', { Id, newPassword });

    }

    // Admin resetting their own password
    public static ResetCurrentAdminPassword(oldPassword: string, newPassword: string): Promise<boolean> {

        return this.apiCaller.PUT<boolean>('ResetCurrentAdminPassword', { newPassword, oldPassword });

    }

    /**
     * Returns JSON array of generated signin links for selected users.
     * @param userProfileIds An array of userProfile ids related to the users.
     */
    public static SetPagePreference(claimUrl: string, claim: string) {

        return this.apiCaller.POST<any>('SetPagePreference', { claim, claimUrl });

    }

    /**
     * Returns JSON array of generated signin links for selected users.
     * @param userProfileIds An array of userProfile ids related to the users.
     */
    public static SetExpiringLicenseSession(show: boolean) {

        return this.apiCaller.POST<any>('SetExpiringLicenseSession', { show });

    }

    /**
     * Checks to see if the username has already been taken by another user
    /**
     * Returns true or false based on if the username is valid
     * @param username A string of the user's username
     */
    public static ValidateUsername(username: string) {

        return this.apiCaller.GET<boolean>('ValidateUsername', { username });

    }

    public static GetBasicInfoByUsername(username: string): Promise<{ FirstName: string; LastName: string }> {

        return this.apiCaller.GET<{ FirstName: string; LastName: string }>('GetBasicInfoByUsername', { username });

    }

    public static GetUsersCustomTests(userProfileId: number): Promise<string[]> {

        return this.apiCaller.GET<string[]>('GetUsersCustomTests', { userProfileId });

    }

    public static RemoveUserFromTC(userProfileId: number, partnerId: number): Promise<FullfilledApiResponse> {

        return this.apiCaller.GET<FullfilledApiResponse>('RemoveUserFromTC', { partnerId, userProfileId });

    }

    /**
     * Impersonates the specified user by navigating directly to the ImpersonateUser endpoint,
     * which sets the spoofed auth cookie and redirects the browser to the main GMetrix site.
     * Uses window.location.href instead of an AJAX call so the browser follows the redirect.
     */
    public static ImpersonateUser(userId: string): void {

        window.location.href = `/Api/Users/ImpersonateUser?userId=${encodeURIComponent(userId)}`;

    }

    /**
     * Resets the preferences for the current user. 
     */
    public static ResetPreferences(): Promise<any> {

        return this.apiCaller.POST<any>('ResetPreferences', {});

    }

    /**
     * Resets the on-screen tours for the current user.
     */
    public static ResetAllTours(): Promise<any> {

        return this.apiCaller.POST<any>('ResetAllTours', {});

    }

    /** Returns pending partner invites for the current user. */
    public static GetPendingPartnerInvites(): Promise<PendingPartnerInvite[]> {

        return this.apiCaller.GET<PendingPartnerInvite[]>('GetPendingPartnerInvites');

    }

    /**
     * Accept or decline a partner invite.
     * @param statusId 2 = Accepted, 3 = Declined
     */
    public static RespondToPartnerInvite(invitationId: number, partnerId: number, statusId: number): Promise<any> {

        return this.apiCaller.POST<Success>('RespondToPartnerInvite', { invitationId, partnerId, statusId });

    }

}
